import { useApolloClient } from '@apollo/client/react';
import { useFormikContext } from 'formik';
import { cloneDeep, get, set, toPath, unset } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import { REMOTE_FORM_STATE_KEY } from '@constants/apiConstants';
import { sendClick, sendScroll } from '@features/Remote/Interaction/query';
import { Appointment } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useAppointmentUserId } from '@store/appointment/selectors';

import { filter } from './filter';
import { getFormTouched, isEmptyForm, pickFormValues } from './formUtils';
import { sendFocus } from './query';
import type { FormTouched, FormValueDiff, FormValues } from './types';

// stable reference so an omitted arg doesn't create a new array every
// render, which would defeat the effect dependency array below
const EMPTY_IGNORE_KEYS: string[] = [];

const handleFocus = (path: string) => {
  const elemFromPath = document
    .evaluate(path, document, null, XPathResult.ANY_TYPE, null)
    .iterateNext() as Element;

  elemFromPath?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

const handleClick = (path: string) => {
  const elemFromPath = document
    .evaluate(path, document, null, XPathResult.ANY_TYPE, null)
    .iterateNext() as HTMLElement;

  elemFromPath?.click();
};

const useSendFocusInteraction = () => {
  const apolloClient = useApolloClient();
  const userId = useAppointmentUserId();
  const { appointment, isSyncedAppointment } = useAppointmentContext();

  return (element: string) => {
    if (!appointment || !appointment?.id || !isSyncedAppointment) return;
    if (appointment.controller.id !== userId) return;

    return apolloClient.mutate({
      mutation: sendFocus,
      variables: {
        element,
        id: appointment.id,
      },
    });
  };
};

const useSendClickInteraction = () => {
  const apolloClient = useApolloClient();
  const userId = useAppointmentUserId();
  const { appointment, isSyncedAppointment } = useAppointmentContext();

  return (element: string) => {
    if (!appointment || !appointment?.id || !isSyncedAppointment) return;
    if (appointment.controller.id !== userId) return;

    return apolloClient.mutate({
      mutation: sendClick,
      variables: {
        element,
        id: appointment.id,
      },
    });
  };
};

const useSendScrollEvent = () => {
  const apolloClient = useApolloClient();
  const { appointment: storedAppointment, hasControl } =
    useAppointmentContext();

  const sendScrollEvent = useCallback(
    (value: string, appointment?: Appointment | null) => {
      if (!appointment?.id && !storedAppointment?.id) return;
      if (!hasControl) return;

      const appointmentId = appointment?.id || storedAppointment?.id || '';
      return apolloClient.mutate({
        mutation: sendScroll,
        variables: { value, id: appointmentId },
      });
    },
    [apolloClient, hasControl, storedAppointment?.id]
  );

  return sendScrollEvent;
};

const useSyncFormField = (
  committedValues?: Record<string, unknown>,
  emptyFormIgnoreKeys: string[] = EMPTY_IGNORE_KEYS
) => {
  const userId = useAppointmentUserId();
  const { appointment, hasControl, isSyncedAppointment } =
    useAppointmentContext();

  const { values, initialValues, resetForm, setValues } =
    useFormikContext<FormValues>();
  const [state, setState] = useSyncState(
    REMOTE_FORM_STATE_KEY,
    values as FormValues
  );

  const updateForm = useCallback(
    (
      newValues: FormValues,
      newTouched: FormTouched,
      shouldValidate = false
    ) => {
      resetForm({
        touched: newTouched as Record<string, boolean | undefined>,
      });
      // The values are set separately because we want to keep the original initial values for reference.
      // E.g. the address search compares the postcode and makes a address list request based on it.
      setValues(newValues, shouldValidate);
    },
    [resetForm, setValues]
  );

  const getFormState = useCallback(
    (state: FormValues) => {
      const newValues = pickFormValues(state, initialValues);
      const newTouched = getFormTouched(state, initialValues);
      return { newValues, newTouched };
    },
    // listening to Formik state changes would cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Passive client — a plain, unconditional mirror of the shared state.
  // Correcting a stale/abandoned edit is handled separately, at the point
  // the active client actually abandons it (see the unmount effect below),
  // so this can stay simple and always reflect live edits as they happen.
  useEffect(() => {
    if (!state || !appointment || hasControl) {
      return;
    }

    const { newValues, newTouched } = getFormState(state);
    updateForm(newValues, newTouched, true);
  }, [appointment, state, hasControl, getFormState, updateForm]);

  // Active client
  useEffect(() => {
    // Sync remote state into empty form on page load
    const isEmpty = isEmptyForm(values, emptyFormIgnoreKeys);

    if (!state || !appointment || !hasControl || !isEmpty) {
      return;
    }

    // Sensitive entries kept in appointment state has mocked values
    // Users need to fill these out every time after a page has loaded
    const filteredState = Object.entries(state).reduce(
      (newState, [key, value]) => {
        if (!filter.includes(key)) {
          return { ...newState, [key]: value };
        }

        return newState;
      },
      {}
    );

    const { newValues, newTouched } = getFormState(filteredState);

    updateForm(newValues, newTouched);
    // listening to Formik state changes would cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appointment,
    state,
    hasControl,
    getFormState,
    updateForm,
    emptyFormIgnoreKeys,
  ]);

  // If this client leaves the form without committing, correct the shared
  // state back to the known-committed values on the way out.
  // Runs only on unmount, so it captures the latest values via a ref
  const latestRef = useRef({
    committedValues,
    appointment,
    isSyncedAppointment,
    userId,
    setState,
  });
  useEffect(() => {
    latestRef.current = {
      committedValues,
      appointment,
      isSyncedAppointment,
      userId,
      setState,
    };
  });

  useEffect(() => {
    return () => {
      const {
        committedValues,
        appointment,
        isSyncedAppointment,
        userId,
        setState,
      } = latestRef.current;

      if (
        !committedValues ||
        !appointment?.id ||
        !isSyncedAppointment ||
        appointment.controller.id !== userId
      ) {
        return;
      }

      setState(
        (prevState) => ({ ...prevState, ...committedValues }) as FormValues
      );
    };
  }, []);

  return (diff: FormValueDiff) => {
    if (!appointment || !isSyncedAppointment || !appointment?.id) return;
    if (appointment.controller.id !== userId) return;

    setState((state) => constructStateFromDiff(state, diff));
  };
};

const handleDiffRemove = (state: FormValues, path: string) => {
  const segments = toPath(path);
  const key = segments[segments.length - 1];
  const parent =
    segments.length > 1 ? get(state, segments.slice(0, -1)) : state;

  // truncate the array, as microdiff removes trailing indicies
  // this prevents having empty array items when they are in fact
  // removed entirely.
  if (Array.isArray(parent) && /^\d+$/.test(key)) {
    parent.length = Math.min(parent.length, Number(key));
  } else {
    unset(state, segments);
  }

  return state;
};

/**
 * take the existing state and the array of diff values from the FormSync
 * component's diff logic and set each field into a new state value.
 *
 * This way, we can send a single `setState` with a new state value from
 * multiple different fields. Prevents having numerous requests updating form
 * state which may cause race conditions as versions are received out of order.
 */
const constructStateFromDiff = (
  state: FormValues,
  stateDiff: FormValueDiff
) => {
  return stateDiff.reduce((newState, { path, val, type }) => {
    let value = val;

    if (type === 'REMOVE') {
      return handleDiffRemove(newState, path);
    }

    if (value === 'true' || value === 'false') {
      value = value === 'true';
    }

    return set(newState, path, value);
  }, cloneDeep(state));
};

export {
  useSendFocusInteraction,
  useSendClickInteraction,
  handleFocus,
  handleClick,
  useSyncFormField,
  useSendScrollEvent,
  constructStateFromDiff,
};
